/**
 * shared/space_campaign.ts — CLIENT MIRROR of server/src/lib/space_campaign.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). Space Wave 4 — persistent operations: the fleet-war clock, the
 * forts, the finite reserves, the attrition round, the receipt points, the
 * objective shift, the incursion grip. Pure.
 */
import { warH } from './space_stations';

// ── THE CLOCK (the sky schedule and the client's visuals share it) ──────────
/** THE ENGAGEMENT LAW (2026-09-17, "the battle only lasted a few seconds"): a war is an HOUR's cycle
 *  with a 30-minute window, a line retreats no earlier than BATTLE_MIN_S in (a line with NO capitals
 *  excepted), attrition runs on the campaign's own clock (one round per ROUND_S), and a side that
 *  committed few capitals retreats at a third of them, not at the absolute three. */
export const BATTLE_PERIOD_S = 3600;
export const BATTLE_LEN_S = 1800;
export const BATTLE_MIN_S = 900;
export const ROUND_S = 60;
export const BATTLE_END_S = 32;
export const BATTLE_CAPS = 10;
export const WAR_SKIRMISHERS = 30;
export const WAR_ZONE_M = 26_000;

export interface FortSpec { key: string; name: string; pos: [number, number, number]; radius: number }
/** The Commons Reach's forts in space_sector.ts POI order (kind fortress | megastation). */
export const SYS0_FORTS: readonly FortSpec[] = [
  { key: 'poi9', name: 'Redhand Fortress', pos: [-1_050_000, 320_000, 850_000], radius: 6_000 },
  { key: 'poi12', name: 'Aurel Prime', pos: [9_500, 900, 5_200], radius: 5_200 },
  { key: 'poi27', name: 'Blackharrow Bastion', pos: [700_000, -320_000, -1_350_000], radius: 7_000 },
];
export function campaignWindow(nowS: number): { cyc: number; t: number } | null {
  const cyc = Math.floor(nowS / BATTLE_PERIOD_S);
  const t = nowS - cyc * BATTLE_PERIOD_S;
  return t > BATTLE_LEN_S ? null : { cyc, t };
}
export function fortOf(cyc: number): FortSpec { return SYS0_FORTS[cyc % SYS0_FORTS.length]; }
export function battleCentre(f: FortSpec): [number, number, number] { return [f.pos[0] + 1.6 * f.radius, f.pos[1] + 400, f.pos[2]]; }
/** The system this cycle's campaign decides (the client's hash, unchanged). */
export function warTargetOf(cyc: number): string { return `sys${1 + (Math.floor(warH(`wt${cyc}`) * 100) % 100)}`; }

// ── THE STORE LAW ────────────────────────────────────────────────────────────
export const CIVS = ['aurelian', 'borean'] as const;
export type Civ = (typeof CIVS)[number];
export const RESERVE_SEED = { hulls: 60, capitals: 12 } as const;
export const RESERVE_HULLS_MAX = 120;
export const RESERVE_CAPS_MAX = 24;
export const YARD_HULLS_TO_RESERVE_PER_H = 6;
/** THE YARD'S INCOME LAW (2026-09-17): the civ's store PAYS the yard for every hull it takes — the only
 *  income a yard has (it pays the miner and escrows supply work; a purse that only drains ran to 1,200 ⓒ). */
export const HULL_SALE_SOV = 2_000n;
export const BOREAN_TRICKLE_HULLS_PER_H = 6;
/** THE RESERVE FLOOR: a civ below this is fed the trickle whatever its yard does, and builds a capital
 *  from ten hulls an hour — an empty store made every war a round-one retreat. */
export const RESERVE_FLOOR = { hulls: 30, capitals: 6 } as const;
export const HULLS_PER_CAPITAL = 10;
/** What a reserve can field: capitals first (hulls convert ten to one when short), then gunships. */
// ── THE ESCORT LAW (2026-09-22): two classes between the fighters and the walls, bought from the same store ──
export const WAR_FRIGATES = 3;        // the MEDIUM ship: a line of three per side at most
export const HULLS_PER_FRIGATE = 4;   // four yard hulls make a frigate
export const WAR_CORVETTES = 6;       // the SMALL ship above the fighter: a screen of six at most
export const HULLS_PER_CORVETTE = 2;  // two yard hulls make a corvette
export const ESCORT_CLASSES = ['frigate', 'corvette'] as const;
export type EscortClass = (typeof ESCORT_CLASSES)[number];
/** The hull-key stem of each escort class (`war<side>:fri<slot>` / `war<side>:cor<slot>`). */
export const ESCORT_KEY: Record<EscortClass, string> = { frigate: 'fri', corvette: 'cor' };
export function commitFromReserve(hulls: number, capitals: number): { caps: number; skirm: number; frigates: number; corvettes: number; hullsLeft: number; capsLeft: number } {
  let caps = Math.min(BATTLE_CAPS, capitals);
  let hullsLeft = hulls;
  while (caps < BATTLE_CAPS && hullsLeft >= HULLS_PER_CAPITAL + WAR_SKIRMISHERS) { caps++; hullsLeft -= HULLS_PER_CAPITAL; }
  const skirm = Math.min(WAR_SKIRMISHERS, hullsLeft);
  hullsLeft -= skirm;
  // The fighters come first (the screen is cheapest — what the economy can supply is what flies in the middle), then
  // what the store can still afford: frigates at four hulls, corvettes at two. A thin store fields fighters alone.
  const frigates = Math.min(WAR_FRIGATES, Math.floor(hullsLeft / HULLS_PER_FRIGATE));
  hullsLeft -= frigates * HULLS_PER_FRIGATE;
  const corvettes = Math.min(WAR_CORVETTES, Math.floor(hullsLeft / HULLS_PER_CORVETTE));
  hullsLeft -= corvettes * HULLS_PER_CORVETTE;
  return { caps, skirm, frigates, corvettes, hullsLeft, capsLeft: capitals - Math.min(BATTLE_CAPS, capitals) };
}

// ── THE ROUND LAW ────────────────────────────────────────────────────────────
export const ROUND_SK_PER_ENEMY_CAP = 0.12;
/** Tuned for the minute-round: ten enemy capitals cost a line ~0.35 capitals a minute, so a full
 *  line reaches the retreat threshold in ~20 minutes (15 is the floor, 30 the window). */
export const ROUND_CAP_P_PER_ENEMY_CAP = 0.035;
/** The escorts' attrition per minute-round: a frigate falls with P 0.02 × enemy capitals (a line of three lasts most
 *  of a full war), a corvette with 0.04 (the screen thins first); both scale with the stances like the fighters. */
export const ROUND_FRI_P_PER_ENEMY_CAP = 0.02;
export const ROUND_COR_P_PER_ENEMY_CAP = 0.04;
export function escortRound(campaignId: bigint, round: number, side: 0 | 1, enemyCaps: number, mult: number): Record<EscortClass, boolean> {
  return {
    frigate: warH(`${campaignId}|${round}|${side}|fri`) < ROUND_FRI_P_PER_ENEMY_CAP * enemyCaps * mult,
    corvette: warH(`${campaignId}|${round}|${side}|cor`) < ROUND_COR_P_PER_ENEMY_CAP * enemyCaps * mult,
  };
}
export const RETREAT_CAPS = 3;
/** A side retreats at RETREAT_CAPS, or at a THIRD of what it committed when that is fewer (a
 *  three-capital line fights to its last one instead of retreating at round one). */
export function retreatThreshold(capsCommitted: number): number {
  return Math.min(RETREAT_CAPS, Math.floor(capsCommitted / 3));
}
export function mayRetreat(capsAlive: number, capsCommitted: number, elapsedS: number, margin = 0): boolean {
  if (capsAlive <= 0) return true;
  return elapsedS >= BATTLE_MIN_S && capsAlive <= retreatThreshold(capsCommitted) + margin; // a WITHDRAW stance retreats a capital earlier
}
/** Rounds owed on the campaign's clock (one per ROUND_S since it opened) beyond those already run. */
export function roundsDue(openedAtS: number, nowS: number, done: number): number {
  return Math.max(0, Math.floor((nowS - openedAtS) / ROUND_S) - done);
}
/** One deterministic attrition round: losses each side takes from the OTHER side's living capitals. */
export function attritionRound(campaignId: bigint, round: number, capsAlive: readonly [number, number], mult: readonly [number, number] = [1, 1]): { sk: [number, number]; cap: [number, number] } {
  const sk: [number, number] = [0, 0], cap: [number, number] = [0, 0];
  for (const s of [0, 1] as const) {
    const enemyCaps = capsAlive[1 - s];
    // Space Wave 7 (FLEET COMMAND): `mult[s]` scales the losses side s TAKES (its stance's `take` × the enemy stance's `give`).
    const exp = ROUND_SK_PER_ENEMY_CAP * enemyCaps * mult[s];
    sk[s] = Math.floor(exp) + (warH(`${campaignId}|${round}|${s}|sk`) < exp - Math.floor(exp) ? 1 : 0);
    cap[s] = warH(`${campaignId}|${round}|${s}|cap`) < ROUND_CAP_P_PER_ENEMY_CAP * enemyCaps * mult[s] ? 1 : 0;
  }
  return { sk, cap };
}

// ── THE RECEIPT / OBJECTIVE LAWS ─────────────────────────────────────────────
export const CAMPAIGN_POINTS = { gunship: 1, corvette: 2, frigate: 4, capital: 10 } as const;
export const CAMPAIGN_POINTS_CAP = 40;
export const CAMPAIGN_PAY_SOV_PER_POINT = 25n;
export const CAMPAIGN_SOV_SHIFT = 12;
export const CAMPAIGN_REAP_S = 7 * 86_400;
/** war<side>:sk<serial> (a gunship) | cap<side>:<k> (a capital). */
export const HULL_KEY_RE = /^(war[01]:(sk\d{1,6}|fri\d{1,2}|cor\d{1,2})|cap[01]:\d{1,2})$/;
export type HullKind = 'gunship' | 'corvette' | 'frigate' | 'capital';
export function hullSide(key: string): 0 | 1 { return key.startsWith('war1') || key.startsWith('cap1') ? 1 : 0; }
export function hullKind(key: string): HullKind {
  if (key.startsWith('cap')) return 'capital';
  const m = /^war[01]:(sk|fri|cor)/.exec(key);
  return m?.[1] === 'fri' ? 'frigate' : m?.[1] === 'cor' ? 'corvette' : 'gunship';
}
export function civOfSide(side: number): Civ { return side === 0 ? 'aurelian' : 'borean'; }

// ── THE ROSTER LAW (2026-09-22) ──────────────────────────────────────────────
/** A capital is an IDENTITY on the campaign (`campaign_capital`: side, slot, alive, killedBy), never only a count:
 *  every client and the economy agree on WHICH hull died and why. `cap<side>:<slot>` names it; `hullSlot` reads it. */
export function hullSlot(key: string): number { const m = /:(?:sk|fri|cor)?(\d+)$/.exec(key); return m ? Number(m[1]) : -1; }
/** Which living escort of a class a round's loss falls on (hashed like the capitals, salted by the class). */
export function pickEscortLoss(campaignId: bigint, round: number, side: 0 | 1, cls: EscortClass, living: readonly number[]): number {
  if (!living.length) return -1;
  const sorted = [...living].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(warH(`${campaignId}|${round}|${side}|${cls}slot`) * sorted.length))];
}
/** Which living capital a round's loss falls on: hashed per campaign, round and side over the living slots (sorted asc). */
export function pickCapitalLoss(campaignId: bigint, round: number, side: 0 | 1, living: readonly number[]): number {
  if (!living.length) return -1;
  const sorted = [...living].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(warH(`${campaignId}|${round}|${side}|slot`) * sorted.length))];
}
/** THE DRAIN LAW: rounds are owed on the campaign's clock up to the finale cutoff (endsAt − BATTLE_END_S) and never past
 *  `now`; a sweep that returns late drains them IN ORDER at their own logical times (≤ ROUND_DRAIN_CAP a sweep) before it
 *  may settle the war — a deadline never skips history, and a backlogged retreat is judged at the minute it happened. */
export const ROUND_DRAIN_CAP = 10;
export function roundsEligible(openedAtS: number, endsAtS: number, nowS: number, done: number): number {
  const cutoff = Math.min(nowS, endsAtS - BATTLE_END_S);
  return Math.max(0, Math.min(ROUND_DRAIN_CAP, Math.floor((cutoff - openedAtS) / ROUND_S) - done));
}
/** The logical time of round k (1-based) on the campaign's clock. */
export function roundAtOf(openedAtS: number, k: number): number { return openedAtS + k * ROUND_S; }

// ── THE INCURSION LAW ────────────────────────────────────────────────────────
export const INCURSION_GRIP_PER_KILL = 2;
export const INCURSION_BOSS_GRIP = 25;
